import React from "react";
import { Link } from "react-router-dom";
import {
  FiHome, FiDollarSign, FiMapPin, FiTrendingUp, FiStar, FiHeart,
  FiArrowRight, FiKey, FiUsers,
} from "react-icons/fi";
import { useApp } from "../context/AppContext";
import properties, { dashboardStats } from "../data/properties";
import Card from "../Components/Card";

const colors = ["", "green", "amber", "red", "purple"];

export default function Dashboard() {
  const { user, favorites } = useApp();

  const favoriteProperties = properties.filter((p) => favorites.includes(p.id));

  return (
    <div className="page-wrapper" style={{ marginTop: "62px" }}>
      {/* Dashboard Header */}
      <div
        className="py-4"
        style={{ background: "var(--gradient-dark)" }}
      >
        <div className="container">
          <div className="d-flex align-items-center gap-3">
            <div className="user-avatar" style={{ width: 50, height: 50, fontSize: "1.2rem" }}>
              {user?.avatar || "U"}
            </div>
            <div>
              <h4 className="text-white fw-bold mb-0">
                Welcome back, {user?.name || "User"}! 👋
              </h4>
              <p className="text-white-50 mb-0 small">
                Here's what's happening with your properties
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {/* Stats Cards */}
        <div className="row g-4 mb-4">
          <div className="col-6 col-lg-3">
            <div className="stat-card fade-in-up">
              <div className="stat-icon blue">
                <FiHome />
              </div>
              <div className="stat-number">{dashboardStats.totalProperties}</div>
              <div className="stat-label">Total Properties</div>
              <div className="stat-change text-success mt-2">↑ 12% this month</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stat-card fade-in-up-delay-1">
              <div className="stat-icon green">
                <FiDollarSign />
              </div>
              <div className="stat-number">${(dashboardStats.avgPrice / 1000).toFixed(0)}k</div>
              <div className="stat-label">Average Price</div>
              <div className="stat-change text-success mt-2">↑ 5% this month</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stat-card fade-in-up-delay-2">
              <div className="stat-icon amber">
                <FiStar />
              </div>
              <div className="stat-number">{dashboardStats.featuredCount}</div>
              <div className="stat-label">Featured</div>
              <div className="stat-change text-warning mt-2">→ Same as last month</div>
            </div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="stat-card fade-in-up-delay-3">
              <div className="stat-icon red">
                <FiHeart />
              </div>
              <div className="stat-number">{favorites.length}</div>
              <div className="stat-label">My Favorites</div>
              <div className="stat-change text-primary mt-2">
                {favorites.length > 0 ? "View saved →" : "Start saving!"}
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="row g-4 mb-4">
          {/* Properties by Location */}
          <div className="col-lg-6">
            <div className="chart-card fade-in-up">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="chart-header mb-0">
                  <FiMapPin className="me-2 text-primary" />
                  Properties by Location
                </div>
              </div>
              {dashboardStats.locationBreakdown
                .filter((l) => l.count > 0)
                .sort((a, b) => b.count - a.count)
                .map((loc, i) => {
                  const maxCount = Math.max(
                    ...dashboardStats.locationBreakdown.map((l) => l.count)
                  );
                  return (
                    <div className="chart-bar" key={loc.name}>
                      <span className="bar-label">{loc.name}</span>
                      <div className="bar-track">
                        <div
                          className={`bar-fill ${colors[i % colors.length]}`}
                          style={{ width: `${(loc.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">{loc.count}</span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Properties by Type */}
          <div className="col-lg-6">
            <div className="chart-card fade-in-up-delay-1">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="chart-header mb-0">
                  <FiKey className="me-2 text-primary" />
                  Properties by Type
                </div>
              </div>
              {dashboardStats.typeBreakdown
                .filter((t) => t.count > 0)
                .sort((a, b) => b.count - a.count)
                .map((type, i) => {
                  const maxCount = Math.max(
                    ...dashboardStats.typeBreakdown.map((t) => t.count)
                  );
                  return (
                    <div className="chart-bar" key={type.name}>
                      <span className="bar-label">{type.name}</span>
                      <div className="bar-track">
                        <div
                          className={`bar-fill ${colors[(i + 2) % colors.length]}`}
                          style={{ width: `${(type.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">{type.count}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        {/* Market Overview */}
        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="chart-card fade-in-up">
              <div className="chart-header">
                <FiTrendingUp className="me-2 text-primary" />
                Market Overview
              </div>
              <div className="d-flex flex-column gap-3">
                <div className="d-flex justify-content-between p-3 rounded-3" style={{ background: "var(--gray-50)" }}>
                  <span className="text-muted">For Sale</span>
                  <span className="fw-bold text-primary">{dashboardStats.totalForSale} properties</span>
                </div>
                <div className="d-flex justify-content-between p-3 rounded-3" style={{ background: "var(--gray-50)" }}>
                  <span className="text-muted">For Rent</span>
                  <span className="fw-bold text-success">{dashboardStats.totalForRent} properties</span>
                </div>
                <div className="d-flex justify-content-between p-3 rounded-3" style={{ background: "var(--gray-50)" }}>
                  <span className="text-muted">Highest Price</span>
                  <span className="fw-bold">${Math.max(...properties.map((p) => p.price)).toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between p-3 rounded-3" style={{ background: "var(--gray-50)" }}>
                  <span className="text-muted">Lowest Price</span>
                  <span className="fw-bold">${Math.min(...properties.map((p) => p.price)).toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between p-3 rounded-3" style={{ background: "var(--gray-50)" }}>
                  <span className="text-muted">Avg Area</span>
                  <span className="fw-bold">{Math.round(properties.reduce((s, p) => s + p.area, 0) / properties.length)} m²</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recently Added */}
          <div className="col-lg-8">
            <div className="activity-table fade-in-up-delay-1">
              <div className="p-3 d-flex justify-content-between align-items-center">
                <h6 className="fw-bold mb-0">
                  <FiUsers className="me-2 text-primary" />
                  Recently Listed Properties
                </h6>
                <Link to="/properties" className="text-decoration-none small">
                  View All <FiArrowRight size={12} />
                </Link>
              </div>
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Location</th>
                      <th>Type</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardStats.recentlyAdded.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <img
                              src={p.image}
                              alt={p.title}
                              className="property-thumb"
                            />
                            <div>
                              <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {p.title}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-muted small">{p.location}</td>
                        <td>
                          <span className="badge bg-light text-dark">{p.type}</span>
                        </td>
                        <td className="fw-semibold text-primary">
                          ${p.price.toLocaleString()}
                        </td>
                        <td>
                          <span
                            className={`badge rounded-pill ${
                              p.status === "For Sale" ? "bg-primary" : "bg-success"
                            }`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/property/${p.id}`}
                            className="btn btn-sm btn-outline-primary rounded-pill"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Favorite Properties */}
        {favoriteProperties.length > 0 && (
          <div className="mb-4">
            <div className="section-header">
              <div>
                <h5 className="section-title">
                  <FiHeart className="me-2 text-danger" />
                  My Favorite Properties
                </h5>
                <p className="section-subtitle">Properties you've saved for later</p>
              </div>
              <Link to="/favorites" className="text-decoration-none">
                View All <FiArrowRight size={12} />
              </Link>
            </div>
            <div className="row g-4">
              {favoriteProperties.slice(0, 4).map((p) => (
                <div className="col-sm-6 col-lg-3" key={p.id}>
                  <Card property={p} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
